import { type DocumentTemplate, TEMPLATE_FIELDS } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

const templateQuery = queryOptions({ queryKey: ['documents', 'template', 'tcle'], queryFn: () => api<DocumentTemplate>('/documents/templates/tcle') });

function Editor({ t }: { t: DocumentTemplate }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(t.title);
  const [body, setBody] = useState(t.body);
  const area = useRef<HTMLTextAreaElement>(null);
  const save = useMutation({
    mutationFn: () => api<DocumentTemplate>('/documents/templates/tcle', { method: 'PUT', body: { title, body } }),
    onSuccess: (n) => qc.setQueryData(templateQuery.queryKey, n),
  });
  const dirty = title !== t.title || body !== t.body;
  const insert = (f: string) => {
    const el = area.current;
    if (!el) return;
    const s = el.selectionStart;
    setBody(body.slice(0, s) + f + body.slice(el.selectionEnd));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + f.length, s + f.length); });
  };
  return (
    <Card className="grid gap-4 p-5">
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-fg-muted">
        {t.isDefault ? <Badge tone="warning">Modelo padrão do sistema</Badge> : <Badge tone="success">Versão {t.version}</Badge>}
        {!t.isDefault && t.createdAt && <span>salva em {new Date(t.createdAt).toLocaleDateString('pt-BR')}{t.createdByName ? ` por ${t.createdByName}` : ''}</span>}
      </div>
      {t.isDefault && <Alert tone="warning" title="Revise antes de usar">O modelo padrão é um ponto de partida. Ajuste com o médico responsável e o jurídico da clínica e salve.</Alert>}
      <Field label="Título"><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} /></Field>
      <div>
        <p className="text-[13px] font-medium text-fg">Campos preenchidos no envio</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(TEMPLATE_FIELDS).map(([k, label]) => (
            <button key={k} type="button" onClick={() => insert(k)} title={label}
              className="h-7 rounded-full border border-border-strong px-2.5 font-mono text-[12px] text-fg-muted hover:text-fg">{k}</button>
          ))}
        </div>
      </div>
      <Field label="Texto do termo">
        <textarea ref={area} value={body} onChange={(e) => setBody(e.target.value)} rows={22} maxLength={60000}
          className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm leading-relaxed text-fg focus:border-primary" />
      </Field>
      {save.error && <Alert tone="danger">{save.error.message}</Alert>}
      <div className="flex items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={!dirty && !t.isDefault} loading={save.isPending}>Salvar nova versão</Button>
        <span className="text-[12.5px] text-fg-muted">Termos já enviados continuam com o texto da época.</span>
      </div>
    </Card>
  );
}

/** Configurações › Documentos: o modelo do TCLE da clínica. */
export function DocumentsSettingsPage() {
  const { data } = useQuery(templateQuery);
  return (
    <div className="grid gap-6">
      <SectionHeader title="Termo de consentimento (TCLE)" description="O texto que o paciente lê e assina na área do paciente. Cada vez que você salva, vira uma nova versão." />
      {data ? <Editor key={`${data.id}-${data.version}`} t={data} /> : <Spinner className="size-5 text-fg-subtle" label="Carregando" />}
    </div>
  );
}
